import React, { useState, useEffect } from "react";
import { ChromePicker } from "react-color";
import { useFormikContext } from "formik";
import get from "lodash/get";
import { COLOR_TRANSFORMS } from "utils/theme";
import ColorField from "components/ColorField";
import { useViewport } from "utils/context";
import Modal from "components/Modal";
import ModalActions from "components/ModalActions";
import ModalContent from "components/ModalContent";
import { useIsMounted } from "utils/hooks";

const DEFAULT_CLASSES = {
  container:
    "flex flex-row justify-between items-center items-stretch relative pa3 w-100",
  label: "f6 fw5 self-start",
  content: "flex flex-column w-100 justify-center",
  description: "f7 self-center pt2",
  pickerContainer: "absolute right-0 bottom-0 z-3",
  pickerScreen: "fixed left-0 top-0 bottom-0 right-0"
};

const ColorPicker = ({
  name,
  modalLabel,
  label,
  description,
  effect,
  footer,
  customClasses,
  modalChildren
}) => {
  const isMounted = useIsMounted();
  const classes = { ...DEFAULT_CLASSES, ...customClasses };
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const [showModal, setShowModal] = useState(false);
  const colorValue = get(values, name);
  const isTouched = get(touched, name);
  const { isSmall } = useViewport();

  const [color, setColor] = useState(colorValue);

  useEffect(() => {
    if (isMounted.current && color !== colorValue) {
      setColor(colorValue);
    }
  }, [colorValue]);

  const handleChange = (color) => {
    setColor(color.hex);
  };
  const handleChangeComplete = (color) => {
    setColor(color.hex);
    setFieldValue(name, color.hex);
    if (effect && effect.name) {
      const transform = COLOR_TRANSFORMS[effect.transform];
      if (transform) {
        const transformedColor = transform(color.hex);
        setFieldValue(effect.name, transformedColor);
      }
    }
    if (!isTouched) {
      setFieldTouched(name, true);
    }
  };

  const colorChromePicker = (
    <ChromePicker
      color={color}
      onChange={handleChange}
      onChangeComplete={handleChangeComplete}
    />
  );

  let modalContent;
  if (isSmall) {
    modalContent = (
      <Modal showModal={showModal} onClose={() => setShowModal(false)}>
        <ModalContent>
          {() => (
            <div className="w-100 ph3">
              <div className="w-100 flex flex-column items-center">
                {modalChildren}
                <div className="flex flex-column pb3 tc">
                  <div className="f5 fw6 pt1">{modalLabel}</div>
                  <div className="f6 pt2 pt0">Tap below to change</div>
                </div>
                {colorChromePicker}
                <ModalActions
                  actions={[
                    {
                      cta: "Done",
                      tag: "button",
                      theme: "slim",
                      onClick: () => setShowModal(false)
                    }
                  ]}
                />
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>
    );
  } else {
    modalContent = (
      <div className={classes.pickerContainer} style={{ top: 60 }}>
        <div
          className={classes.pickerScreen}
          onClick={() => setShowModal(false)}
        ></div>
        {colorChromePicker}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div className={classes.label}>{label}</div>
        {description && (
          <div className={classes.description}>{description}</div>
        )}
        {footer}
      </div>
      <ColorField color={color} onClick={() => setShowModal(true)} />
      {showModal && modalContent}
    </div>
  );
};

export default ColorPicker;
